import { type HTMLAttributes, Children, type FC, type Ref } from "react";
import { isObject, mergeClasses } from "@huub-web/utils";

import { childStyle, stackStyle } from "./Stack.css";
import type { RequiredChildrenProp } from "../../props";

import { Reveal, type RevealProps } from "../Reveal/Reveal";

export type StackProps = RequiredChildrenProp &
  HTMLAttributes<HTMLDivElement> & {
    overflowHidden?: boolean;
    childClassName?: string;
    reveal?: Omit<RevealProps, "children"> | boolean;
    grow?: boolean;
    wrap?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A stack component that stacks its children vertically.
 */
export const Stack: FC<StackProps> = ({
  overflowHidden,
  childClassName,
  reveal = false,
  grow = false,
  wrap = true,
  className,
  children,
  ref,
  ...props
}) => (
  <div
    ref={ref}
    className={mergeClasses(stackStyle({ overflowHidden, grow }), className)}
    {...props}
  >
    {Children.map(children, (child) =>
      reveal ? (
        <Reveal
          {...(isObject(reveal) ? reveal : {})}
          className={mergeClasses(
            isObject(reveal) ? reveal.className : undefined,
            childStyle({ overflowHidden }),
            childClassName,
          )}
        >
          {child}
        </Reveal>
      ) : wrap ? (
        <div
          className={mergeClasses(
            childStyle({ overflowHidden }),
            childClassName,
          )}
        >
          {child}
        </div>
      ) : (
        child
      ),
    )}
  </div>
);
